import React from 'react'
import { Box, IconButton, InputBase, Stack, Tooltip } from '@mui/material'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'

type Props = {
    input: string
    onInputChange: (value: string) => void
    onSend: () => void
    onAbort: () => void
    loading: boolean
    canStop: boolean
    disabled: boolean
    placeholder?: string
}

const ACTION_SIZE = 26

function enabledActionSx(isDark: boolean) {
    return {
        width: ACTION_SIZE,
        height: ACTION_SIZE,
        borderRadius: 1,
        bgcolor: isDark ? 'common.white' : 'grey.900',
        color: isDark ? 'grey.900' : 'common.white',
        '&:hover': {
            bgcolor: isDark ? 'grey.200' : 'grey.800',
        },
    }
}

export default function ChatComposer({
    input,
    onInputChange,
    onSend,
    onAbort,
    loading,
    canStop,
    disabled,
    placeholder = 'Ask e.g. stocks with RSI(14) > 70',
}: Props) {
    const showStop = loading && canStop
    const canSend = !!input.trim() && !disabled && !loading

    return (
        <Box
            sx={{
                flexShrink: 0,
                px: 2,
                py: 1.25,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.625,
                    minHeight: ACTION_SIZE + 12,
                    bgcolor: 'background.default',
                    transition: 'border-color 0.15s',
                    '&:focus-within': { borderColor: 'primary.main' },
                }}
            >
                <InputBase
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder={placeholder}
                    value={input}
                    onChange={(e) => onInputChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            if (canSend) {
                                onSend()
                            }
                        }
                    }}
                    disabled={disabled}
                    sx={{
                        flex: 1,
                        alignSelf: input.includes('\n') ? 'flex-end' : 'center',
                        px: 0,
                        py: 0,
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        '& textarea': {
                            resize: 'none',
                            padding: '6px 0',
                        },
                    }}
                />
                {showStop ? (
                    <Tooltip title="Stop generating">
                        <IconButton
                            onClick={onAbort}
                            aria-label="Stop generating"
                            size="small"
                            sx={(theme) => ({
                                flexShrink: 0,
                                alignSelf: input.includes('\n') ? 'flex-end' : 'center',
                                ...enabledActionSx(theme.palette.mode === 'dark'),
                                borderRadius: '50%',
                            })}
                        >
                            <StopRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Tooltip title={canSend ? 'Send · Enter' : 'Type a message'}>
                        <span>
                            <IconButton
                                onClick={onSend}
                                disabled={!canSend}
                                aria-label="Send"
                                size="small"
                                sx={(theme) => ({
                                    flexShrink: 0,
                                    alignSelf: input.includes('\n') ? 'flex-end' : 'center',
                                    width: ACTION_SIZE,
                                    height: ACTION_SIZE,
                                    borderRadius: 1,
                                    ...(canSend
                                        ? enabledActionSx(theme.palette.mode === 'dark')
                                        : {
                                              bgcolor: 'action.disabledBackground',
                                              color: 'action.disabled',
                                              '&:hover': { bgcolor: 'action.disabledBackground' },
                                              '&.Mui-disabled': {
                                                  bgcolor: 'action.disabledBackground',
                                                  color: 'action.disabled',
                                              },
                                          }),
                                })}
                            >
                                <ArrowUpwardRoundedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>
        </Box>
    )
}
